import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import aviso, { SEM_SERVIDOR } from '../lib/aviso';
import { LuPlus, LuSearch, LuPencil, LuEye, LuTrash2, LuChevronLeft, LuChevronRight, LuSparkles } from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import RecordPanel, { PanelSection, Switch } from '../components/RecordPanel';
import SecaoHistorico from '../components/historico/SecaoHistorico';
import { ListRow, PerPage, RowAction, RowActions, RowConfirm, CardConfirm, ListCard, ListCards } from '../components/ListKit';
import '../components/ListKit.css';
import { useCelular } from '../hooks/useCelular';
import { useRecorte } from '../hooks/useRecorte';
import './TiposContatoPage.css';
import { TipoContatoService, type TipoContato } from '../services/TipoContatoService';

const POR_PAGINA = [25, 50, 100];
type Modo = 'ver' | 'editar';

const dataCurta = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

const vazio: TipoContato = { tipoContatoId: 0, nome: '', comoReconhecer: '', entraNoFunil: true };

const TiposContatoPage = () => {
    const celular = useCelular();
    const [tipos, setTipos] = useState<TipoContato[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [recorte, gravarRecorte] = useRecorte('tc:recorte', {
        busca: '', pagina: 1, porPagina: 25,
    });
    const [busca, setBusca] = useState(recorte.busca);
    const [pagina, setPagina] = useState(recorte.pagina);
    const [porPagina, setPorPagina] = useState(recorte.porPagina);
    useEffect(() => {
        gravarRecorte({ busca, pagina, porPagina });
    }, [busca, pagina, porPagina, gravarRecorte]);

    const [aberto, setAberto] = useState<TipoContato | null>(null);
    const [modo, setModo] = useState<Modo>('ver');
    const [rascunho, setRascunho] = useState<TipoContato>(vazio);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');
    const [aConfirmar, setAConfirmar] = useState<number | null>(null);

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            const r = await TipoContatoService.getAll();
            if (r.sucesso) setTipos(r.dados || []);
            else aviso.erro(r.mensagem || 'Não foi possível carregar os tipos.');
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [busca, porPagina]);

    const filtrados = useMemo(() => {
        const t = busca.trim().toLowerCase();
        return t
            ? tipos.filter(x => (x.nome || '').toLowerCase().includes(t)
                || (x.comoReconhecer || '').toLowerCase().includes(t))
            : tipos;
    }, [tipos, busca]);

    const totalPaginas = Math.max(1, Math.ceil(filtrados.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = filtrados.slice(inicio, inicio + porPagina);

    const abrirVer = (t: TipoContato) => { setAberto(t); setModo('ver'); setErro(''); };
    const abrirEdicao = (t: TipoContato) => { setAberto(t); setRascunho(t); setModo('editar'); setErro(''); };
    const abrirNovo = () => { setAberto(vazio); setRascunho(vazio); setModo('editar'); setErro(''); };
    const fechar = () => { setAberto(null); setErro(''); };

    const ehNovo = modo === 'editar' && aberto?.tipoContatoId === 0;

    const salvar = async () => {
        const nome = (rascunho.nome || '').trim();
        if (!nome) { setErro('Informe o nome do tipo.'); return; }

        /* Dois tipos com o mesmo nome não quebram a API, mas deixam a pessoa escolhendo
           entre opções idênticas na ficha — e o assistente sem saber qual é qual. */
        const repetido = tipos.some(t =>
            t.tipoContatoId !== aberto?.tipoContatoId &&
            (t.nome || '').trim().toLowerCase() === nome.toLowerCase());
        if (repetido) { setErro('Já existe um tipo com esse nome.'); return; }

        setSalvando(true);
        try {
            const corpo = {
                nome,
                comoReconhecer: (rascunho.comoReconhecer || '').trim() || null,
                entraNoFunil: rascunho.entraNoFunil,
            };
            const r = ehNovo
                ? await TipoContatoService.create(corpo)
                : await TipoContatoService.update(aberto!.tipoContatoId, corpo);
            if (r.sucesso) {
                aviso.ok(ehNovo ? 'Tipo criado.' : 'Tipo atualizado.');
                await carregar(); fechar();
            } else aviso.erro(r.mensagem || 'Não foi possível salvar o tipo.');
        } catch {
            aviso.erro('Não foi possível salvar o tipo.');
        } finally {
            setSalvando(false);
        }
    };

    const excluir = async (alvo = aberto) => {
        if (!alvo?.tipoContatoId) return;
        setSalvando(true);
        try {
            const r = await TipoContatoService.delete(alvo.tipoContatoId);
            if (r.sucesso) { aviso.ok(r.mensagem || 'Tipo excluído.'); await carregar(); fechar(); }
            else aviso.erro(r.mensagem || 'Não foi possível excluir o tipo.');
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setSalvando(false);
            setAConfirmar(null);
        }
    };

    const criarSugeridos = async () => {
        setSalvando(true);
        try {
            const r = await TipoContatoService.criarSugeridos();
            if (r.sucesso) { aviso.ok('Tipos sugeridos criados.'); await carregar(); }
            else aviso.erro(r.mensagem || 'Não foi possível criar os tipos sugeridos.');
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setSalvando(false);
        }
    };

    const COLUNAS = '1.1fr 2.4fr 130px 78px';

    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">
                {tipos.length === 0 ? 'Nenhum tipo cadastrado' : 'Nada com esse termo'}
            </div>
            <p className="lk-blank-text">
                {tipos.length === 0
                    ? 'Sem tipos, toda conversa entra no funil igual — fornecedor, corretor e lead no mesmo lugar. Comece pela lista sugerida e ajuste o que não servir.'
                    : 'Ajuste a busca para ver os outros.'}
            </p>
            {tipos.length === 0 && (
                <div className="tc-blank-acoes">
                    <button type="button" className="lk-btn is-primary" onClick={criarSugeridos} disabled={salvando}>
                        <LuSparkles size={14} /> Usar os tipos sugeridos
                    </button>
                    <button type="button" className="lk-btn" onClick={abrirNovo}>
                        <LuPlus size={14} /> Criar do zero
                    </button>
                </div>
            )}
        </div>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
            <span className="lk-range">
                {inicio + 1}–{Math.min(inicio + porPagina, filtrados.length)} de {filtrados.length}
            </span>
            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                    <LuChevronLeft size={13} />
                </button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                    <LuChevronRight size={13} />
                </button>
            </span>
        </div>
    );

    const selo = (t: TipoContato) => (
        <span className={`tc-selo ${t.entraNoFunil ? 'is-sim' : 'is-nao'}`}>
            {t.entraNoFunil ? 'Sim' : 'Não'}
        </span>
    );

    return (
        <div className="page-shell lk-screen">
            <PageHeader
                title="Tipos de contato"
                subtitle="O que cada pessoa é para a sua empresa. O tipo decide se ela entra no funil de vendas."
                actions={!isLoading && tipos.length === 0 ? undefined :
                    <div className="lk-actions">
                        <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                            <LuPlus size={14} /> Novo tipo
                        </button>
                    </div>
                }
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input type="search" placeholder="Buscar tipo…"
                            value={busca} onChange={e => setBusca(e.target.value)} />
                    </label>
                    <span className="lk-count">
                        {tipos.length} {tipos.length === 1 ? 'tipo' : 'tipos'}
                    </span>
                </div>

                {celular ? (
                    <>
                        <ListCards>
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(t => (
                                    aConfirmar === t.tipoContatoId ? (
                                        <CardConfirm key={t.tipoContatoId}
                                            question={`Excluir "${t.nome}"?`}
                                            busy={salvando}
                                            onCancel={() => setAConfirmar(null)}
                                            onConfirm={() => excluir(t)} />
                                    ) : (
                                        <ListCard
                                            key={t.tipoContatoId}
                                            titulo={t.nome}
                                            apoio={t.entraNoFunil ? 'Entra no funil' : 'Fora do funil'}
                                            selected={aberto?.tipoContatoId === t.tipoContatoId}
                                            label={`Abrir ${t.nome}`}
                                            onOpen={() => abrirVer(t)}
                                            acoes={
                                                <RowAction icon={<LuTrash2 size={15} />} label="Excluir tipo" danger
                                                    onClick={() => setAConfirmar(t.tipoContatoId)} />
                                            }
                                        />
                                    )
                                ))}
                        </ListCards>
                        {!isLoading && filtrados.length === 0 && listaVazia}
                        {!isLoading && filtrados.length > 0 && rodape}
                    </>
                ) : (
                <div className="lk-table">
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                            <div className="lk-head">Nome</div>
                            <div className="lk-head">Como o assistente reconhece</div>
                            <div className="lk-head">Entra no funil</div>
                            <div className="lk-head lk-head-acts">Ações</div>
                        </div>
                        <div className="lk-body">
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(t => (
                                    aConfirmar === t.tipoContatoId ? (
                                        <RowConfirm key={t.tipoContatoId}
                                            question={`Excluir "${t.nome}"?`}
                                            busy={salvando}
                                            onCancel={() => setAConfirmar(null)}
                                            onConfirm={() => excluir(t)} />
                                    ) : (
                                        <ListRow key={t.tipoContatoId} columns={COLUNAS}
                                            selected={aberto?.tipoContatoId === t.tipoContatoId}>
                                            <span className="lk-cell lk-strong"><span>{t.nome}</span></span>
                                            <span className="lk-cell lk-muted"><span>{t.comoReconhecer || '—'}</span></span>
                                            <span className="lk-cell"><span>{selo(t)}</span></span>
                                            <RowActions>
                                                <RowAction icon={<LuEye size={14} />} label="Ver tipo"
                                                    onClick={() => abrirVer(t)} />
                                                <RowAction icon={<LuTrash2 size={14} />} label="Excluir tipo" danger
                                                    onClick={() => setAConfirmar(t.tipoContatoId)} />
                                            </RowActions>
                                        </ListRow>
                                    )
                                ))}
                        </div>
                    </div>

                    {!isLoading && filtrados.length === 0 && listaVazia}
                    {!isLoading && filtrados.length > 0 && rodape}
                </div>
                )}
            </div>

            <RecordPanel
                open={!!aberto}
                onClose={fechar}
                title={modo === 'editar'
                    ? ((rascunho.nome || '').trim() || (ehNovo ? 'Novo tipo' : 'Tipo de contato'))
                    : (aberto?.nome ?? '')}
                subtitle={aberto && !ehNovo ? `Criado em ${dataCurta(aberto.dataCriacao)}` : undefined}
                editing={modo === 'editar'}
                onBack={ehNovo ? undefined : () => setModo('ver')}
                actions={aberto && !ehNovo && (
                    <button type="button" className="rp-btn is-primary" onClick={() => abrirEdicao(aberto)}>
                        <LuPencil size={13} /> Editar
                    </button>
                )}
                footer={
                    <>
                        {!ehNovo && (
                            <button type="button" className="rp-btn is-danger" onClick={() => excluir()} disabled={salvando}>
                                Excluir
                            </button>
                        )}
                        <button type="button" className="rp-btn" disabled={salvando}
                            onClick={() => (ehNovo ? fechar() : setModo('ver'))}>Cancelar</button>
                        <button type="button" className="rp-btn is-primary" onClick={salvar} disabled={salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </button>
                    </>
                }
            >
                {modo === 'ver' && aberto && (
                    <>
                        <PanelSection title="Identificação">
                            <div className="tc-kv">
                                <span className="tc-k">Nome</span>
                                <span className="tc-v">{aberto.nome}</span>
                                <span className="tc-k">Como o assistente reconhece</span>
                                <span className="tc-v">{aberto.comoReconhecer || <span className="tc-vazio">não informado</span>}</span>
                            </div>
                        </PanelSection>
                        <PanelSection title="Comportamento">
                            <p className="tc-texto">
                                {aberto.entraNoFunil
                                    ? 'Entra no funil de vendas: a conversa aparece na Pipeline e conta nas métricas.'
                                    : 'Fora do funil: a conversa fica só no Chat, sem entrar na Pipeline nem contar nas métricas.'}
                            </p>
                        </PanelSection>
                    </>
                )}

                {modo === 'editar' && (
                    <PanelSection title="Identificação">
                        <div className="rp-field">
                            <label className="rp-label" htmlFor="tc-nome">Nome</label>
                            <input id="tc-nome" className="rp-input" type="text" autoFocus
                                value={rascunho.nome} placeholder="Ex.: Fornecedor"
                                onChange={e => { setRascunho(r => ({ ...r, nome: e.target.value })); setErro(''); }}
                                onKeyDown={e => { if (e.key === 'Enter') salvar(); }} />
                            {erro
                                ? <span className="rp-error">{erro}</span>
                                : <span className="tc-hint">É o que aparece na ficha do contato e no painel do Chat.</span>}
                        </div>

                        <div className="rp-field">
                            <label className="rp-label" htmlFor="tc-como">Como o assistente reconhece</label>
                            <textarea id="tc-como" className="rp-input tc-area" rows={3}
                                value={rascunho.comoReconhecer || ''}
                                placeholder="Ex.: Oferece produto ou serviço para nós, manda orçamento, cobra nota"
                                onChange={e => setRascunho(r => ({ ...r, comoReconhecer: e.target.value }))} />
                            <span className="tc-hint">
                                Esta frase vai para o assistente. Quanto mais parecida com a vida real, melhor ele acerta.
                            </span>
                        </div>

                        <div className="rp-toggle-row">
                            <div className="rp-toggle-text">
                                <span className="rp-toggle-label">Entra no funil de vendas</span>
                                <span className="rp-toggle-hint">
                                    Desligado, o contato fica só no Chat e não conta na Pipeline nem nas métricas.
                                </span>
                            </div>
                            <Switch
                                checked={rascunho.entraNoFunil}
                                label="Entra no funil de vendas"
                                onChange={(v: boolean) => setRascunho(r => ({ ...r, entraNoFunil: v }))}
                            />
                        </div>
                    </PanelSection>
                )}

                {modo === 'ver' && aberto && !ehNovo && (
                    <SecaoHistorico entidade="tipo_contato" id={aberto.tipoContatoId} />
                )}
            </RecordPanel>
        </div>
    );
};

export default TiposContatoPage;
